import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { availabilityService } from '../services/availabilityService';

const AvailabilityManager = ({ student }) => {
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAvailability = useCallback(async () => {
    try {
      setLoading(true);
      const data = await availabilityService.getUserAvailabilityByUserId(student.id);
      
      // Filter only available slots
      const availableSlots = data.filter(slot => slot.isAvailable);
      setAvailability(availableSlots);
    } catch (err) {
      console.error('Error loading availability:', err);
    } finally {
      setLoading(false);
    }
  }, [student?.id]);

  // Load availability data
  useEffect(() => {
    if (student?.id) {
      loadAvailability();
    }
  }, [student?.id, loadAvailability]);

  const formatTime = (time) => {
    // Parse the time string (expected format: "HH:mm" or "HH:mm:ss")
    const [hours, minutes] = time.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return time; // Return original if parsing fails
    }
    
    // Convert to 12-hour format
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    // Format minutes with leading zero if needed
    const displayMinutes = minutes.toString().padStart(2, '0');
    
    return `${displayHours}:${displayMinutes} ${period}`;
  };

  const formatDay = (day) => {
    return day.charAt(0) + day.slice(1).toLowerCase();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <ScheduleIcon fontSize="small" />
        Availability:
      </Typography>
      {availability.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {availability.map((slot, index) => (
            <Chip
              key={index}
              label={`${formatDay(slot.dayOfWeek)} ${formatTime(slot.startTime)} - ${formatTime(slot.endTime)} (${slot.locationName})`}
              color="primary"
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No availability set
        </Typography>
      )}
    </Box>
  );
};

export default AvailabilityManager; 